import { Box, Chip, Divider, Typography } from '@mui/material';
import { FC } from 'react';
import { DocSection } from '../docs-components';

interface ChangeEntry {
  version: string;
  date: string;
  type: 'added' | 'changed' | 'deprecated' | 'fixed' | 'removed';
  description: string;
}

const changes: ChangeEntry[] = [
  { version: 'v1.4.0', date: '2026-02-10', type: 'added', description: 'New `device.connected` and `device.disconnected` webhook events.' },
  {
    version: 'v1.4.0',
    date: '2026-02-10',
    type: 'added',
    description: 'Historical data endpoint now supports `interval` parameter (`hourly`, `daily`, `monthly`).'
  },
  {
    version: 'v1.3.2',
    date: '2026-01-15',
    type: 'fixed',
    description: 'Fixed incorrect `outdoorTemperature` value returned for Genesis Ultra devices in certain configurations.'
  },
  { version: 'v1.3.2', date: '2026-01-15', type: 'fixed', description: 'Webhook delivery retries now correctly apply exponential back-off.' },
  { version: 'v1.3.0', date: '2025-12-01', type: 'added', description: 'Control endpoint: support for setting `hotWaterBoost` mode.' },
  {
    version: 'v1.3.0',
    date: '2025-12-01',
    type: 'changed',
    description: 'Rate limit window for historical data reduced from 5 to 1 minute to better protect backend.'
  },
  {
    version: 'v1.2.1',
    date: '2025-10-20',
    type: 'fixed',
    description: 'Authentication error responses now consistently include the `requestId` field.'
  },
  { version: 'v1.2.0', date: '2025-09-05', type: 'added', description: 'Webhook signature verification via `X-Thermia-Signature` header.' },
  {
    version: 'v1.2.0',
    date: '2025-09-05',
    type: 'deprecated',
    description: '`GET /v1/devices/:id/temps` is deprecated in favour of `GET /v1/devices/:id/status`.'
  },
  { version: 'v1.1.0', date: '2025-07-11', type: 'added', description: 'Initial public release of webhook management endpoints.' },
  { version: 'v1.0.0', date: '2025-05-01', type: 'added', description: 'Initial public release of the Thermia API v1.' }
];

const typeColors: Record<ChangeEntry['type'], 'success' | 'info' | 'warning' | 'error' | 'default'> = {
  added: 'success',
  changed: 'info',
  deprecated: 'warning',
  fixed: 'default',
  removed: 'error'
};

const grouped = changes.reduce<Record<string, ChangeEntry[]>>((acc, entry) => {
  if (!acc[entry.version]) acc[entry.version] = [];
  acc[entry.version].push(entry);
  return acc;
}, {});

const Changelog: FC = () => (
  <Box>
    <Typography variant="h4" fontWeight={700} gutterBottom>
      Changelog
    </Typography>
    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
      A history of notable changes to the Thermia API. We follow{' '}
      <a href="https://semver.org" target="_blank" rel="noreferrer">
        Semantic Versioning
      </a>
      .
    </Typography>

    {Object.entries(grouped).map(([version, entries], idx) => (
      <Box key={version}>
        {idx > 0 && <Divider sx={{ my: 3 }} />}
        <DocSection title={`${version}  —  ${entries[0].date}`}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {entries.map((entry, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Chip
                  label={entry.type.toUpperCase()}
                  color={typeColors[entry.type]}
                  size="small"
                  variant="outlined"
                  sx={{ minWidth: 86, mt: 0.2 }}
                />
                <Typography variant="body2">{entry.description}</Typography>
              </Box>
            ))}
          </Box>
        </DocSection>
      </Box>
    ))}
  </Box>
);

export default Changelog;
